import {
  Card,
  Col,
  Form,
  Input,
  Layout,
  Row,
  Table,
  TableColumnsType,
  TableProps
} from "antd";
import { get, toNumber } from "lodash";
import includes from "lodash/includes";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FormActionButton from "src/components/common/FormActionButton";
import { messages } from "src/constants/messages";
import { apiCaller, cancelListToken } from "src/util/apiCaller";
import { MessageStatusError } from "src/util/MessageStatus";

const FormItem = Form.Item;
const initialState = {
  tenVaiTro: "",
  maVaiTro: "",
  lstQuyen: ""
};

type TableRowSelection<T> = TableProps<T>["rowSelection"];

interface DataType {
  key: React.Key;
  tenQuyen: string;
}

const VaiTroForm = ({ status }: any) => {
  const { isActive } = status;
  const navigate = useNavigate();
  const location = useLocation();
  const [type, setType] = useState("new");
  const [fieldTouch, setFieldTouch] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingButtonClose, setLoadingButtonClose] = useState(false);
  const [form] = Form.useForm();
  const { tenVaiTro, maVaiTro } = initialState;
  const [dataQuyen, setDataQuyen] = useState([]);
  const [isLoadTable, setIsLoadTable] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([""]);
  const [selectedRows, setSelectedRows] = useState([""]);

  const { validateFields, resetFields, setFieldsValue } = form;
  const { id } = useParams();

  const columns: TableColumnsType<DataType> = [
    { title: "Tên quyền", dataIndex: "tenQuyen", key: "tenQuyen" }
  ];

  useEffect(() => {
    if (isActive) {
      const load = () => {
        if (includes(location.pathname, "them-moi")) {
          setType("new");
        } else {
          setType("edit");
          getInfo();
        }
      };
      getListDataQuyen();
      load();
    } else {
      navigate("/error/401");
    }
  }, []);

  useEffect(() => {
    return () => {
      cancelListToken(["infoVaiTro", "listQuyen"]);
    };
  }, []);

  /**
   * getListDataQuyen: lấy danh sách quyền
   * @memberof VaiTroForm
   */
  const getListDataQuyen = async () => {
    setIsLoadTable(true);
    const configAPI = {
      url: `api/quyens`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "listQuyen"
    } as any;
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const listQuyen = get(response.data, "content", []);
        setDataQuyen(listQuyen);
        setIsLoadTable(false);
      } else {
        MessageStatusError(toNumber(response?.status));
        setIsLoadTable(false);
      }
    } finally {
      setIsLoadTable(false);
    }
  };

  /**
   * getInfo : Lấy thông tin
   * @memberof  VaiTroForm
   */
  const getInfo = async () => {
    if (location.state && location.state.itemData) {
      // Nếu có dữ liệu truyền sang
      const res = location.state.itemData;
      setFieldsValue({
        vaitro: res
      });
      setSelectedRowKeys(
        res.quyens ? res.quyens.map((item: any) => item.id) : []
      );
      setSelectedRows(res.quyens);
    } else {
      const configAPI = {
        url: `api/vaitros/${id}`,
        method: "GET",
        value: "",
        apiType: "DETAIL",
        getName: "infoVaiTro"
      };
      const response = await apiCaller(configAPI);
      try {
        if (
          response?.data &&
          (response.status === 200 || response.status === 201)
        ) {
          setFieldsValue({
            vaitro: response.data
          });
          setSelectedRowKeys(
            response.data.quyens
              ? response.data.quyens.map((item: any) => item.id)
              : []
          );
          setSelectedRows(response.data.quyens);
        } else {
          MessageStatusError(toNumber(response?.status));
        }
      } catch (error) {
        // Xử lý lỗi khi lưu dữ liệu không thành công
      }
    }
  };

  /**
   * onChange: chọn table
   * @param {Array} newSelectedRowKeys
   * @param {Array} selectedRows
   * @memberof VaiTroForm
   */
  const onChange = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(selectedRows);
  };

  /**
   * onChange: chọn table
   * @param {Array} selected
   * @param {Array} selectedRows
   * @memberof VaiTroForm
   */
  const onSelect = (selected: any, selectedRows: any) => {
    setSelectedRowKeys(selected);
    setSelectedRows(selectedRows);
  };

  /**
   * rowSelection: Chọn trên bảng
   * @param {object} selectedRowKeys
   * @param {object} selectedRows
   * @memberof VaiTroForm
   */
  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    hideSelectAll: false,
    onChange: onChange,
    onSelect: onSelect
  };

  /**
   * goBack: quay lại trang danh sách
   * @memberof VaiTroForm
   */
  const goBack = () => {
    navigate("/quan-tri/vai-tro");
  };

  /**
   * save: lưu dữ liệu
   * @memberof VaiTroForm
   */
  const save = () => {
    validateFields()
      .then((values) => {
        setLoadingButton(true);
        saveData(values, false);
      })
      .catch(() => {
        setLoadingButton(false);
      });
  };

  /**
   * saveAndClose: Lưu và đóng
   * @memberof VaiTroForm
   */
  const saveAndClose = () => {
    validateFields()
      .then((values) => {
        setLoadingButton(true);
        saveData(values, true);
      })
      .catch(() => {
        setLoadingButton(false);
      });
  };

  /**
   * saveData: goi api lưu dữ liệu
   * @param {object} values
   * @param {boolean} saveQuit
   * @memberof QuanLyLoaiBaoCao
   */
  const saveData = async (values: any, saveQuit = false) => {
    const vaiTro = values.vaitro;
    const lstQuyen = selectedRows.map((item: any) => item.tenQuyen).toString();
    const newData = { ...vaiTro, lstQuyen };
    try {
      let configAPI;
      if (type === "new") {
        configAPI = {
          url: `api/vaitros/create`,
          method: "POST",
          data: newData,
          apiType: "ADD",
          isShowMessage: true
        };
      } else {
        configAPI = {
          url: `api/vaitros/${id}`,
          method: "PUT",
          data: newData,
          apiType: "EDIT",
          isShowMessage: true
        };
      }

      const response = await apiCaller(configAPI);
      if (response?.status === 200 || response?.status === 201) {
        if (saveQuit) {
          goBack();
          setLoadingButtonClose(false);
        } else {
          if (type === "new") {
            resetFields();
          }
          setFieldTouch(false);
          setLoadingButton(false);
        }
      } else {
        MessageStatusError(toNumber(response?.status));
        setLoadingButton(false);
      }
    } catch (error) {
      setLoadingButton(false);
    }
  };

  const formTitle = type === "new" ? "Thêm mới vai trò" : "Chỉnh sửa vai trò";
  return (
    <Layout>
      <div style={{ display: "flex" }}>
        <Col md={12}>
          <h2 style={{ color: "black", fontSize: 20, marginTop: 10 }}>
            {formTitle}
          </h2>
        </Col>
      </div>
      <Card>
        <Form
          className="form-container wrapper-content-inner"
          layout="vertical"
          form={form}
          name="vaitro-control"
          onFieldsChange={() => setFieldTouch(true)}
        >
          <Row gutter={24}>
            <Col md={12} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Mã vai trò"
                name={["vaitro", "maVaiTro"]}
                rules={[
                  {
                    type: "string",
                    required: true,
                    message: messages.ERROR_REQUIRED
                  }
                ]}
                initialValue={maVaiTro}
              >
                <Input className="input-item" placeholder="Nhập mã vai trò" />
              </FormItem>
            </Col>
            <Col md={12} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Tên vai trò"
                name={["vaitro", "tenVaiTro"]}
                rules={[
                  {
                    type: "string",
                    required: true,
                    message: messages.ERROR_REQUIRED
                  }
                ]}
                initialValue={tenVaiTro}
              >
                <Input className="input-item" placeholder="Nhập tên vai trò" />
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem
                style={{ display: "block" }}
                initialValue={selectedRowKeys}
              >
                <Table
                  bordered
                  rowSelection={rowSelection}
                  columns={columns}
                  rowKey={"id"}
                  dataSource={dataQuyen}
                  style={{ cursor: "pointer" }}
                  rowClassName={(record, index) =>
                    index % 2 === 0 ? "qti-table-row-background" : ""
                  }
                  pagination={false}
                  loading={isLoadTable}
                  size="middle"
                  className="qti-table-wrapper gx-table-responsive"
                  scroll={{ x: 1000 }}
                />
              </FormItem>
            </Col>
          </Row>
          <hr />
          <FormActionButton
            goBack={goBack}
            saveAndClose={saveAndClose}
            disabled={fieldTouch}
            save={save}
            type={type}
            loadingButton={loadingButton}
            loadingButtonClose={loadingButtonClose}
          />
        </Form>
      </Card>
    </Layout>
  );
};

export default VaiTroForm;
